/**
 * Token program addresses for SPL Token and Token-2022
 * These addresses are the same across all Solana networks (mainnet, devnet, testnet)
 */
export const TOKEN_PROGRAM_ADDRESS = "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA";
export const TOKEN_2022_PROGRAM_ADDRESS = "TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb";
export const COMPUTE_BUDGET_PROGRAM_ADDRESS = "ComputeBudget111111111111111111111111111111";
export const MEMO_PROGRAM_ADDRESS = "MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr";

/**
 * Swig smart wallet program address and instruction discriminators.
 * Swig wraps inner instructions (e.g. SPL transferChecked) inside a SignV2
 * instruction and executes them via CPI using the Swig PDA as authority.
 * See: https://github.com/anagram-xyz/swig
 */
export const SWIG_PROGRAM_ADDRESS = "swigypWHEksbC64pWKwah1WTeh9JXwx8H1rJHLdbQMB";
export const SWIG_SIGN_V2_DISCRIMINATOR = 11; // U16 LE

/**
 * Secp256r1 precompile program address (used by Swig passkey wallets)
 * Swig transactions may include secp256r1 signature verification instructions
 * before the SignV2 instruction. These are filtered out during transaction flattening.
 */
export const SECP256R1_PRECOMPILE_ADDRESS = "Secp256r1SigVerify1111111111111111111111111";

/**
 * Phantom/Solflare Lighthouse program address
 * Phantom and Solflare wallets inject Lighthouse instructions for user protection on mainnet transactions.
 * - Phantom adds 1 Lighthouse instruction (4th instruction)
 * - Solflare adds 2 Lighthouse instructions (4th and 5th instructions)
 * We allow these as optional instructions to support these wallets.
 * See: https://github.com/x402-foundation/x402/issues/828
 */
export const LIGHTHOUSE_PROGRAM_ADDRESS = "L2TExMFKdjpN9kozasaurPirfHy9P8sbXoAN1qA3S95";

/**
 * Default RPC URLs for Solana networks
 */
export const DEVNET_RPC_URL = "https://api.devnet.solana.com";
export const TESTNET_RPC_URL = "https://api.testnet.solana.com";
export const MAINNET_RPC_URL = "https://api.mainnet-beta.solana.com";
export const DEVNET_WS_URL = "wss://api.devnet.solana.com";
export const TESTNET_WS_URL = "wss://api.testnet.solana.com";
export const MAINNET_WS_URL = "wss://api.mainnet-beta.solana.com";

/**
 * USDC token mint addresses (default stablecoin)
 */
export const USDC_MAINNET_ADDRESS = "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v";
export const USDC_DEVNET_ADDRESS = "4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU";
export const USDC_TESTNET_ADDRESS = "4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU"; // Same as devnet

/**
 * Compute budget configuration
 * All prices are in microlamports (1 lamport = 1,000,000 microlamports)
 */
export const DEFAULT_COMPUTE_UNIT_PRICE_MICROLAMPORTS = 1;
export const DEFAULT_COMPUTE_UNIT_LIMIT = 20_000;
export const MAX_MEMO_BYTES = 256;

/**
 * Security limits to prevent fee amplification / DoS attacks.
 * These limits protect the facilitator from malicious payloads that attempt
 * to inflate gas costs while the facilitator pays fees.
 */

/**
 * Maximum compute unit price in microlamports.
 * Kept intentionally low to prevent bots from inflating priority fees
 * at the facilitator's expense (the facilitator pays gas).
 * Default: 5 microlamports. Override via MAX_SVM_COMPUTE_UNIT_PRICE_MICROLAMPORTS environment variable.
 */
export const MAX_COMPUTE_UNIT_PRICE_MICROLAMPORTS = process.env
  .MAX_SVM_COMPUTE_UNIT_PRICE_MICROLAMPORTS
  ? parseInt(process.env.MAX_SVM_COMPUTE_UNIT_PRICE_MICROLAMPORTS, 10)
  : 5;

/**
 * Maximum compute unit limit.
 * SPL token transfer with memo uses ~20k CU. 60k provides 3x safety margin.
 * Wallets like Solflare add ~40k CU buffer, so we need headroom.
 * Prevents attackers from inflating CU budget at facilitator's expense.
 * Override via MAX_SVM_COMPUTE_UNIT_LIMIT environment variable.
 */
export const MAX_COMPUTE_UNIT_LIMIT = process.env.MAX_SVM_COMPUTE_UNIT_LIMIT
  ? parseInt(process.env.MAX_SVM_COMPUTE_UNIT_LIMIT, 10)
  : 60_000;

/**
 * Maximum allowed required signatures.
 * Typical x402 flow: payer + fee payer = 2 signatures.
 * Higher values indicate multi-sig spam attempts.
 */
export const MAX_REQUIRED_SIGNATURES = 2;

/**
 * How long a transaction is held in the duplicate settlement cache (ms).
 * Covers the Solana blockhash lifetime (~60-90s) with margin.
 */
export const SETTLEMENT_TTL_MS = 120_000;

/**
 * Solana address validation regex (base58, 32-44 characters)
 */
export const SVM_ADDRESS_REGEX = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

/**
 * CAIP-2 network identifiers for Solana (V2)
 */
export const SOLANA_MAINNET_CAIP2 = "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp";
export const SOLANA_DEVNET_CAIP2 = "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1";
export const SOLANA_TESTNET_CAIP2 = "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z";

/**
 * V1 to V2 network identifier mappings (for backwards compatibility)
 * V1 used simple names like solana, V2 uses CAIP-2
 */
export const V1_TO_V2_NETWORK_MAP: Record<string, string> = {
  solana: SOLANA_MAINNET_CAIP2,
  "solana-devnet": SOLANA_DEVNET_CAIP2,
  "solana-testnet": SOLANA_TESTNET_CAIP2,
};
